import React, { useState, useEffect, useCallback } from 'react';
import { 
  Bell, 
  X, 
  AlertTriangle, 
  CheckCircle,
  DollarSign,
  User,
  RefreshCw,
  Trash2
} from 'lucide-react';
import { getAuthHeaders } from '@/utils/getauthheaders';

// Constants
const API_BASE_URL = `${import.meta.env?.VITE_BACKEND_URL}`;
const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes
const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

// Interfaces
interface DriverCashSummary {
  driver_id: number;
  driver_name: string;
  pending_amount: number;
  collected_amount: number;
  pending_count: number;
  collected_count: number;
  last_updated?: string | Date;
  notifications_dismissed?: boolean;
}

interface CashStatistics {
  total_drivers: number;
  total_pending: number;
  total_collected: number;
  drivers_with_pending: number;
  drivers_with_high_pending: number;
}

interface ApiResponse {
  success: boolean;
  message: string;
  data: DriverCashSummary[];
  statistics: CashStatistics;
  timestamp: string;
}

interface CashNotification {
  id: string;
  type: 'warning' | 'info' | 'success';
  title: string;
  message: string;
  driver: DriverCashSummary;
  timestamp: Date;
  read: boolean;
}

const CashNotificationsSystem: React.FC = () => {
  const [notifications, setNotifications] = useState<CashNotification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [error, setError] = useState<string | null>(null);
  const [statistics, setStatistics] = useState<CashStatistics | null>(null);
  const [cachedData, setCachedData] = useState<{data: DriverCashSummary[], timestamp: number} | null>(null);
  const [dismissedNotifications, setDismissedNotifications] = useState<Set<string>>(new Set());
  const [readNotifications, setReadNotifications] = useState<Set<string>>(new Set());

  // Simple cache check
  const isCacheValid = useCallback(() => {
    return cachedData && (Date.now() - cachedData.timestamp < CACHE_DURATION);
  }, [cachedData]);

  // Real data using your actual API endpoint
  const getRealData = async () => {
    const response = await fetch(`${API_BASE_URL}/api/drivers/cash-summary`, {
      method: 'GET',
      headers: {
        ...getAuthHeaders(),
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result: ApiResponse = await response.json();

    if (!result.success) {
      throw new Error(result.message || 'Failed to fetch data');
    }

    return {
      drivers: result.data,
      statistics: result.statistics
    };
  };

  // Mock data fallback
  const getMockData = () => ({
    drivers: [
      {
        driver_id: 1,
        driver_name: "Ion Popescu",
        pending_amount: 245.50,
        collected_amount: 180.00,
        pending_count: 3,
        collected_count: 2,
        notifications_dismissed: false
      },
      {
        driver_id: 2,
        driver_name: "Maria Ionescu", 
        pending_amount: 125.75,
        collected_amount: 650.25,
        pending_count: 2,
        collected_count: 8,
        notifications_dismissed: false
      },
      {
        driver_id: 3,
        driver_name: "Alexandru Dumitrescu",
        pending_amount: 45.00,
        collected_amount: 320.50,
        pending_count: 1,
        collected_count: 4,
        notifications_dismissed: false
      }
    ] as DriverCashSummary[],
    statistics: {
      total_drivers: 3,
      total_pending: 416.25,
      total_collected: 1150.75,
      drivers_with_pending: 3,
      drivers_with_high_pending: 2
    }
  });

  // Fetch cash summary data
  const fetchDriverCashSummary = useCallback(async (): Promise<{drivers: DriverCashSummary[], statistics: CashStatistics}> => {
    // Return cached data if valid
    if (isCacheValid()) {
      const stats = statistics || {
        total_drivers: 0,
        total_pending: 0,
        total_collected: 0,
        drivers_with_pending: 0,
        drivers_with_high_pending: 0
      };
      return { drivers: cachedData!.data, statistics: stats };
    }

    console.log('🔄 Fetching cash summary from API...');
    
    try {
      // Try to use real API first
      const realData = await getRealData();
      
      // Update cache
      setCachedData({
        data: realData.drivers,
        timestamp: Date.now()
      });

      return realData;
    } catch (apiError) {
      console.warn('API call failed, using mock data:', apiError);
      
      // Simulate API delay for demo
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Use mock data as fallback
      const mockData = getMockData();
      
      // Update cache
      setCachedData({
        data: mockData.drivers,
        timestamp: Date.now()
      });

      return mockData;
    }
  }, [cachedData, isCacheValid, statistics]);

  // Generate notifications based on cash data
  const generateNotifications = useCallback((drivers: DriverCashSummary[]): CashNotification[] => {
    const newNotifications: CashNotification[] = [];
    const timestamp = new Date();

    drivers.forEach(driver => {
      const { driver_id, driver_name, pending_amount, pending_count, collected_amount, collected_count, notifications_dismissed } = driver;
      
      // Skip drivers who have dismissed notifications or have no activity
      if (notifications_dismissed || (pending_amount === 0 && collected_amount === 0)) {
        return;
      }

      // Generate unique notification keys
      const generateNotificationKey = (type: string, driverId: number) => 
        `${type}-${driverId}-${pending_amount}-${collected_amount}`;

      // High pending amount warning (over 200 RON)
      if (pending_amount > 200) {
        const notificationKey = generateNotificationKey('urgent', driver_id);
        if (!dismissedNotifications.has(notificationKey)) {
          newNotifications.push({
            id: notificationKey,
            type: 'warning',
            title: 'URGENT: Sumă foarte mare neîncasată',
            message: `${driver_name} are ${pending_amount.toFixed(2)} RON neîncasați din ${pending_count} cursă${pending_count !== 1 ? '/e' : ''}`,
            driver,
            timestamp,
            read: readNotifications.has(notificationKey)
          });
        }
      }
      // Medium pending amount warning (100-200 RON)
      else if (pending_amount > 100) {
        const notificationKey = generateNotificationKey('warning', driver_id);
        if (!dismissedNotifications.has(notificationKey)) {
          newNotifications.push({
            id: notificationKey,
            type: 'warning',
            title: 'Sumă mare neîncasată',
            message: `${driver_name} are ${pending_amount.toFixed(2)} RON neîncasați din ${pending_count} cursă${pending_count !== 1 ? '/e' : ''}`,
            driver,
            timestamp,
            read: readNotifications.has(notificationKey)
          });
        }
      }
      
      // Success notification for good collection performance (over 500 RON collected)
      if (collected_amount > 500 && collected_count >= 3) {
        const notificationKey = generateNotificationKey('success', driver_id);
        if (!dismissedNotifications.has(notificationKey)) {
          newNotifications.push({
            id: notificationKey,
            type: 'success',
            title: 'Performanță excelentă',
            message: `${driver_name} a încasat ${collected_amount.toFixed(2)} RON din ${collected_count} curse`,
            driver,
            timestamp,
            read: readNotifications.has(notificationKey)
          });
        }
      }
    });

    return newNotifications.sort((a, b) => {
      // Sort by priority: warning > info > success
      const priority = { warning: 3, info: 2, success: 1 };
      return priority[b.type] - priority[a.type];
    });
  }, [dismissedNotifications, readNotifications]);

  // Load cash data and generate notifications
  const loadCashData = useCallback(async () => {
    if (loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const { drivers, statistics: newStats } = await fetchDriverCashSummary();
      const newNotifications = generateNotifications(drivers);
      
      setNotifications(newNotifications);
      setStatistics(newStats);
      setLastRefresh(new Date());
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [loading, fetchDriverCashSummary, generateNotifications]);

  // Initial load and auto-refresh setup
  useEffect(() => {
    loadCashData();
    
    const interval = setInterval(loadCashData, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  // Mark notification as read
  const markAsRead = useCallback((notificationId: string) => {
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
    setReadNotifications(prev => new Set([...prev, notificationId]));
  }, []);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId: string) => {
    const notification = notifications.find((n) => n.id === notificationId);
    if (!notification) return;

    try {
      // Dismiss all cash notifications for this driver in backend
      const response = await fetch(
        `${API_BASE_URL}/api/drivers/${notification.driver.driver_id}/dismiss-cash-notifications`,
        {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
          },
        }
      );

      if (!response.ok) {
        throw new Error('Dismiss cash notifications failed');
      }

      // If backend succeeded → update local state
      setNotifications((prev) =>
        prev.filter((n) => n.driver.driver_id !== notification.driver.driver_id)
      );
      setDismissedNotifications(
        (prev) => new Set([...prev, notificationId])
      );
    } catch (error) {
      console.error('Failed to dismiss cash notifications:', error);

      // fallback local remove
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      setDismissedNotifications(
        (prev) => new Set([...prev, notificationId])
      );
    }
  }, [notifications]);

  // Get notification icon and colors
  const getNotificationConfig = (type: string) => {
    switch (type) {
      case 'warning':
        return { 
          icon: <AlertTriangle className="w-4 h-4" />, 
          iconColor: 'text-red-500',
          bgColor: 'bg-white',
          borderColor: 'border-l-red-400'
        };
      case 'success':
        return { 
          icon: <CheckCircle className="w-4 h-4" />, 
          iconColor: 'text-green-500',
          bgColor: 'bg-white',
          borderColor: 'border-l-green-400'
        };
      default:
        return { 
          icon: <CheckCircle className="w-4 h-4" />, 
          iconColor: 'text-blue-500',
          bgColor: 'bg-blue-50',
          borderColor: 'border-l-blue-400'
        };
    }
  };

  // Calculate counts
  const unreadCount = notifications.filter(n => !n.read).length;
  const urgentCount = notifications.filter(n => !n.read && n.type === 'warning' && n.title.includes('URGENT')).length;

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-25 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      <div className="relative">
        {/* Notification Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="relative p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
          aria-label={`${unreadCount} notificări${urgentCount > 0 ? ` (${urgentCount} urgente)` : ''}`}
        >
          <Bell className="w-5 h-5" />
          
          {unreadCount > 0 && (
            <span className={`absolute -top-1 -right-1 ${urgentCount > 0 ? 'bg-red-500 animate-pulse' : 'bg-blue-500'} text-white text-xs rounded-full h-4 w-4 flex items-center justify-center font-medium`}>
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        {/* Notifications Panel */}
        {isOpen && (
          <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 lg:w-[420px] bg-white rounded-lg shadow-xl border border-slate-200 z-50 max-h-[90vh] overflow-hidden">
            
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <div>
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-green-600" />
                  Notificări Cash
                </h3>
                
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={loadCashData}
                  disabled={loading}
                  className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded disabled:opacity-50"
                  aria-label="Reîmprospătează"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded"
                  aria-label="Închide"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Error State */}
            {error && (
              <div className="p-4 bg-red-50 border-b border-red-100">
                <p className="text-sm text-red-600">⚠️ {error}</p>
                <button
                  onClick={loadCashData}
                  className="text-xs text-red-700 hover:text-red-800 font-medium mt-1"
                >
                  Încearcă din nou
                </button>
              </div>
            )}

            {/* Notifications List */}
            <div className="max-h-96 overflow-y-auto">
              {loading && notifications.length === 0 && (
                <div className="p-8 text-center">
                  <RefreshCw className="animate-spin mx-auto mb-2 w-6 h-6 text-slate-400" />
                  <p className="text-sm text-slate-500">Se încarcă...</p>
                </div>
              )}

              {!loading && notifications.length === 0 && !error && (
                <div className="p-8 text-center">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-400" />
                  <p className="text-sm font-medium text-slate-600">Totul în regulă!</p>
                  <p className="text-xs text-slate-500">Nu există notificări</p>
                </div>
              )}

              {notifications.map((notification, index) => {
                const config = getNotificationConfig(notification.type);
                
                return (
                  <div
                    key={notification.id}
                    className={`p-4 ${index !== notifications.length - 1 ? 'border-b border-slate-100' : ''} ${
                      !notification.read ? `${config.bgColor} border-l-4 ${config.borderColor}` : 'hover:bg-slate-50'
                    } transition-colors`}
                  >
                    {/* Notification Header */}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 flex-1">
                        <div className={config.iconColor}>
                          {config.icon}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-medium text-sm text-slate-800 leading-tight">
                            {notification.title}
                          </h4>
                          <p className="text-xs text-slate-500 mt-1">
                            {notification.message}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => deleteNotification(notification.id)}
                        className="text-slate-400 hover:text-red-500 transition-colors p-1 ml-2"
                        aria-label="Șterge notificarea"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {/* Driver Info Card */}
                    <div className="bg-white rounded-lg p-3 border border-slate-200">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-6 h-6 bg-slate-100 rounded-full flex items-center justify-center">
                          <User className="w-3 h-3 text-slate-600" />
                        </div>
                        <span className="font-medium text-sm text-slate-800">{notification.driver.driver_name}</span>
                      </div>
                      
                      <div className="grid grid-cols-1 gap-3">
                        {/* Pending Amount */}
                        <div className="text-center bg-red-50 rounded-lg p-2">
                          <div className="text-xs text-red-600 mb-1">Neîncasat</div>
                          <div className="font-bold text-red-700 text-lg">
                            {notification.driver.pending_amount.toFixed(0)}
                          </div>
                          <div className="text-xs text-red-600">
                            {notification.driver.pending_count} cursă{notification.driver.pending_count !== 1 ? '/e' : ''}
                          </div>
                        </div>
                        
                        
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-between items-center mt-3 pt-2 border-t border-slate-100">
                      <span className="text-xs text-slate-400">
                        {notification.timestamp.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {!notification.read && (
                        <button
                          onClick={() => markAsRead(notification.id)}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                        >
                          Citit ✓
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            
          </div>
        )}
      </div>
    </>
  );
};

export default CashNotificationsSystem;